// src/pages/reservations/CreateReservation.tsx
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ReservationType } from "../../../types/Library";
import { createReservation } from "../../../services/library/reservation";
import { routes } from "../../../routes";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import SelectComponent from "../../../components/inputs/Select/SelectComponent";
import toast from "react-hot-toast";
import { ReservationFormValidation } from "./ReservationFormValidation";
import DateFieldComponent from "../../../components/inputs/Datefield/DatefieldComponent";
import { useGetBooksQuery } from "../../../hooks/library/useGetBooksQuery";
import { useAppDispatch } from "../../../hooks/hooks";
import { debounce } from "lodash";
import { resetUserData, setUserData } from "../../../redux/slices/users/user";
import { getUserByBI, getUserByNuit } from "../../../services/user/getUsers";
import { key_userBi, key_userNuit } from "../../../utils/constants";
import { UserType } from "../../../types/User";
import { UserPreview } from "../../Users/UserPreview";

export const CreateReservation = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const mutation = useMutation({ mutationFn: createReservation, mutationKey: "createReservation" });
    const { data: booksData, status: statusBooks, isLoading: isLoadingBooks, isError: isErrorBooks, refetch: refreshBooks } = useGetBooksQuery({
        PageSize: 4000,
        PageNumber: 1,
    });
    
    type ReservationFormValues = ReservationType & {
        user?: UserType;
        searchNuit?: string,
        searchBi?: string,
    };
    
    const defaultValues: ReservationFormValues = {
        idBook: "",
        idUser: "",
        reservationDate: new Date().toISOString(),
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
    };
    const { control, handleSubmit, formState: { errors } ,getValues,reset,setValue} = useForm<ReservationFormValues>({
        resolver: yupResolver(ReservationFormValidation),
        defaultValues,
        mode: "onBlur",
    });

    const onSubmit = handleSubmit(async (data) => {
        try {
            await mutation.mutateAsync(data);
            toast.success("Reserva criada com sucesso!");
            handleBack();
        } catch (error: any) {
            toast.error(`Erro: ${error.message}`);
        }
    }, (errors) => {

        const idUserError = errors.idUser?.message;

        console.log(idUserError);

        if (idUserError) {
            toast.error(
                "Por favor, procure o utilizador preenchendo o BI ou o NUIT."
            );
        } else {
            toast.error("Ocorreu um erro ao submeter o formulário.");
        }

    });

    const handleBack = () =>{
        dispatch(resetUserData());
        navigate(routes.reservations);
    } 

    const { isLoading: loadingUserByBi, refetch: fetchUserByBi, } = useQuery(
        [key_userBi],
        async () => {
            try {
                const userData = await getUserByBI(getValues('searchBi')!);
                if (!userData) return;
                reset({
                    searchBi: getValues('searchBi')!,
                    idUser: userData.id,
                    userFullname: userData.name,
                    status: true,
                    user: userData,
                    reservationDate: new Date().toISOString(),
                    startDate: new Date().toISOString(),
                    endDate: new Date().toISOString(),
                })
                dispatch(setUserData(userData));
                return userData;
            } catch (error) {
                dispatch(resetUserData());
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );
    const { isLoading: loadingUserByNuit, refetch: fetchUserByNuit, } = useQuery(
        [key_userNuit],
        async () => {
            try {
                const userData = await getUserByNuit(getValues('searchNuit')!);
                if (!userData) return;
                reset({
                    searchBi: getValues('searchBi')!,
                    idUser: userData.id,
                    userFullname: userData.name,
                    status: true,
                    user: userData,
                    reservationDate: new Date().toISOString(),
                    startDate: new Date().toISOString(),
                    endDate: new Date().toISOString(),
                })
                dispatch(setUserData(userData));
                return userData;
            } catch (error) {
                dispatch(resetUserData());
            }
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            retry: false
        }
    );


    const handleBIChange = useCallback(
        debounce((value: string) => {
            const trimmedValue = value.trim();
            setValue('searchBi', trimmedValue);
            if (trimmedValue.length >= 10) {
                fetchUserByBi();
            } else {
            }
        }, 300),
        [setValue
            , fetchUserByBi
        ]
    );

    const handleNUITChange = useCallback(
        debounce((value: string) => {
            const trimmedValue = value.trim();
            setValue('searchNuit', trimmedValue);
            if (trimmedValue.length >= 8) {
                fetchUserByNuit();
            } else {
            }
        }, 300),
        [setValue
            , fetchUserByNuit
        ]
    );
    return (
        <form onSubmit={onSubmit} noValidate>
            <Card size="1">
                <Flex direction="column" gap="4">

                <Flex gap='2'>
                        <TextFieldComponent
                            style={{ width: '100%' }}
                            name="searchBi"
                            label="Procurar por BI"
                            control={control}
                            errors={errors}
                            fieldProps={{
                                placeholder: 'Procurar por BI',
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleBIChange(e.target.value),
                                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleBIChange(pastedText);
                                }
                            }}
                        />
                        <TextFieldComponent
                            style={{ width: '100%' }}
                            name="searchNuit"
                            label="Procurar por NUIT"
                            control={control}
                            errors={errors}
                            fieldProps={{
                                placeholder: 'Procurar por NUIT',
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleNUITChange(e.target.value),
                                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleNUITChange(pastedText);
                                }
                            }}
                        />
                    </Flex>
                    {loadingUserByNuit || loadingUserByBi ? <Spinner loading></Spinner>
                        : <UserPreview />
                    }
                    
                <SelectComponent
                            label="Livro"
                            options={booksData?.items.map((book) => ({
                                value: book?.id ?? "",
                                label: `${book?.title}`
                            }))}
                            errors={errors}
                            control={control}
                            name="idBook"
                            itemsLoading={isLoadingBooks}
                            itemsError={isErrorBooks}
                            style={{ width: '100%' }}
                            required={true}
                        />
                    <Flex direction='row' gap='2'>
                        <DateFieldComponent
                            name="startDate"
                            label="Data de Início"
                            control={control}
                            errors={errors}
                            fieldProps={{ placeholder: "Data de Início" }}
                            style={{ width: '100%' }}

                        />
                        <DateFieldComponent
                            name="endDate"
                            label="Data de Término"
                            control={control}
                            errors={errors}
                            fieldProps={{ placeholder: "Data de Término" }}
                            style={{ width: '100%' }}

                        />
                    </Flex>
                    <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                        <Button type="button" variant="soft" color="gray" onClick={handleBack} disabled={mutation.isLoading}>
                            <ChevronLeftIcon /> Voltar
                        </Button>
                        <Button type="submit" disabled={mutation.isLoading}>
                            {mutation.isLoading ? <Spinner loading /> : <EnterIcon />}
                            Registar
                        </Button>
                    </Flex>
                </Flex>
            </Card>
        </form>
    );
};
